import { api, clearTokens, refreshSessionTokens, setTokens, isAuthenticationRejection } from './api';
import type { ApiRequestConfig } from './api';
import { FILTER_SESSION_KEYS } from '../constants/filterSession';
import { clearGlbCache } from './cadGlbCache';
import { LoginRequest, LoginResponse, User, ApiError } from '../types';

// Response type for password operations
interface PasswordOperationResult {
  success: boolean;
  error?: ApiError;
}

const clearFilterSession = () => {
  for (const key of FILTER_SESSION_KEYS) {
    try { sessionStorage.removeItem(key); } catch { /* Storage may be disabled. */ }
  }
};

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
export const LOGIN_TIMEOUT_MS = 60_000;

// Authorization header authentication with token storage
// Cookie認証は廃止され、sessionStorage でトークンを管理
class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Backend returns access_token and refresh_token in response body
    // App Service can spend close to the shared 30-second API timeout
    // resuming a cold backend instance. Keep the broader API default strict,
    // but allow login enough time to survive that one-time platform delay.
    const response = await api.post<any>('/auth/login', credentials, {
      timeout: LOGIN_TIMEOUT_MS,
    });

    clearFilterSession();
    setTokens(response.access_token, response.refresh_token, response.expires_in);

    let user: User;
    try {
      user = await this.getCurrentUser({ skipRateLimitRetry: true });
    } catch (error) {
      clearTokens();
      throw error;
    }

    return {
      user,
      tokens: {
        accessToken: response.access_token || '',
        refreshToken: response.refresh_token || '',
        expiresIn: response.expires_in || 3600,
      },
    };
  }

  async logout(): Promise<void> {
    try {
      // Backend invalidates the token
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearFilterSession();
      clearGlbCache();
      clearTokens();
    }
  }

  async getCurrentUser(config?: ApiRequestConfig): Promise<User> {
    // Authorization header is automatically sent via api interceptor
    const response = await api.get<any>('/auth/me', config);

    // Map API response to User type
    // API returns: { id, email, name, tenant_id, roles: [], groups: [] }
    // User type expects: { id, email, fullName, tenantId, role, roles, ... }
    const user: User = {
      id: response.id,
      email: response.email,
      fullName: response.name || response.fullName || '',
      display_name: response.display_name,
      role: response.roles?.[0] || response.role || 'user',  // Take first role or default to 'user'
      roles: response.roles || [],
      tenantId: response.tenant_id || response.tenantId || '',
      tenantCode: response.tenant_code || response.tenantCode || '',
      isActive: response.is_active ?? response.isActive ?? true,
      createdAt: response.created_at || response.createdAt || new Date().toISOString(),
      updatedAt: response.updated_at || response.updatedAt || new Date().toISOString(),
      groups: response.groups || [],
      permissions: response.permissions || [],
      deniedPermissions: response.denied_permissions || response.deniedPermissions || [],
      allowedTenants: response.allowed_tenants || response.allowedTenants || [],
      tenantSettings: response.tenant_settings || response.tenantSettings,
    };

    return user;
  }

  async refreshToken(): Promise<LoginResponse> {
    if (!sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
      throw new Error('No refresh token available');
    }
    const response = await refreshSessionTokens();

    // Fetch updated user info
    const user = await this.getCurrentUser();

    return {
      user,
      tokens: {
        accessToken: response.access_token || '',
        refreshToken: response.refresh_token || '',
        expiresIn: response.expires_in || 3600,
      },
    };
  }

  async requestPasswordReset(data: { email: string; tenantCode: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/forgot-password', {
        email: data.email,
        tenant_code: data.tenantCode,
      });
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password reset request failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  async confirmPasswordReset(data: { token: string; password: string; confirmPassword: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/reset-password', {
        token: data.token,
        new_password: data.password,
        confirm_password: data.confirmPassword,
      });
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password reset confirmation failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  async changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/change-password', data);
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password change failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  // Check authentication by attempting to get current user
  // HttpOnly cookies don't allow JS access, so we validate via API call
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  // Get access token from sessionStorage
  getAuthToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Returns the user rather than a boolean so the caller does not have to fetch
   * the same record again to populate the store. null is falsy, so callers that
   * only test validity are unaffected.
   */
  async validateSession(): Promise<User | null> {
    try {
      return await this.getCurrentUser();
    } catch (error) {
      if (isAuthenticationRejection(error)) return null;
      throw error;
    }
  }
}

export const authService = new AuthService();
